import { Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { ResetPasswordModel } from '../../models/account.model';
import { AccountService } from '../../services/account.service';
import { DynamicFormControl, DynamicForm, DynamicFormValidator } from '../../models/dynamic.model';
import { PasswordStrengthValidator } from '../../validators/password-strength.validator';

import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';


@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html'
})
export class ResetPasswordComponent {
  form: DynamicForm = new DynamicForm();
  reactiveForm: FormGroup;
  success = false;
  error = false;
  identifier: string = null;
  key: string = null;

  constructor(
    private accountService: AccountService,
    protected messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute) {

  }


  ngOnInit() {
    // Initialize reactive form for better validation support
    this.reactiveForm = new FormGroup({
      newPassword: new FormControl('', 
        [
          Validators.required,
          PasswordStrengthValidator.strong(8)
        ]
      ),
      confirmNewPassword: new FormControl('', [Validators.required])
    }, {
      validators: PasswordStrengthValidator.matchPassword('newPassword', 'confirmNewPassword')
    });



    this.form.title = 'Reset password';
    this.form.key = 'resetForm';
    this.form.submitLabel = 'Reset Password';
    this.form.buttons.push({ label: 'Cancel', key: 'cancel' });

    this.form.addRow();

    let c = this.form.addControl('newPassword', 'password', 'New Password', null, true, null, 12);
    c.minLength = 8; // Updated to 8 characters minimum
    // Note: Additional password strength validation (uppercase, lowercase, digits, special chars)
    // and breached password checking is enforced on the server side

    c = this.form.addControl('confirmNewPassword', 'password', 'Confirm New Password', null, true, null, 12);
    c.minLength = 8; // Updated to match new password requirements

    let validators: any[] = [];

    let validator = new DynamicFormValidator();
    validator.type = 'crossfield';
    validator.value = {
      sourceKey: 'confirmNewPassword',
      targetKey: 'newPassword',
      condition: '==',
      message: 'New password does not match confirmation'
    };

    validators.push(validator);

    validator = new DynamicFormValidator();
    validator.type = 'crossfield';
    validator.value = {
      sourceKey: 'password',
      targetKey: 'newPassword',
      condition: '!=',
      message: 'New password must be different from old password'
    };

    validators.push(validator);


    this.form.validators = validators;

    this.route.params.subscribe(p => {
      if (p['identifier'] != null) {
        this.identifier = p['identifier'];
      } else {
        this.router.navigateByUrl('/resetPassword');
      }
    });

    this.route.queryParams.subscribe(p => {
      if (p['key'] != null) {
        this.key = p['key'];
      }
    });

  }

  resetPassword(form: any) {
    const password = this.getPasswordValue(form);
    
    if (!this.isFormValid(form)) {
      this.markFormFieldsAsTouched();
      return;
    }

    if (!password) {
      this.error = true;
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'Please enter a new password' 
      });
      return;
    }

    this.performPasswordReset(password);
  }

  private getPasswordValue(form: any): string {
    if (this.reactiveForm?.valid) {
      return this.reactiveForm.value.newPassword;
    }
    if (form?.valid) {
      return form.value.newPassword;
    }
    return null;
  }

  private isFormValid(form: any): boolean {
    return (this.reactiveForm?.valid) || (form?.valid);
  }

  private markFormFieldsAsTouched(): void {
    if (this.reactiveForm) {
      Object.keys(this.reactiveForm.controls).forEach(key => {
        this.reactiveForm.get(key)?.markAsTouched();
      });
    }
  }

  private performPasswordReset(password: string): void {
    const model = new ResetPasswordModel();
    model.RequestIdentifier = this.identifier;
    model.RequestCode = this.key;
    model.NewPassword = password;

    this.accountService.resetPassword(model)
      .subscribe({
        next: (response) => {
          if (response instanceof HttpErrorResponse || response.isError) {
            this.handleResetError();
          } else {
            this.handleResetSuccess();
          }
        },
        error: (error) => {
          console.error('Password reset error:', error);
          this.handleResetError();
        }
      });
  }

  private handleResetSuccess(): void {
    this.success = true;
    this.error = false;
    this.messageService.add({ 
      severity: 'success', 
      summary: 'Success', 
      detail: 'Password updated successfully' 
    });
    this.router.navigate(['/login']);
  }

  private handleResetError(): void {
    this.error = true;
    this.success = false;
    this.messageService.add({ 
      severity: 'error', 
      summary: 'Error', 
      detail: 'Failed to reset password. Please try again or contact support.' 
    });
  }

  clickButton(key: string) {
    if (key === 'cancel') {
      this.router.navigate(['/login']);
    }
  }
}
